// Renderer-neutral time-window intent shared by the Bookmap variants and the
// TradingView price chart.
//
// Only semantic intent is persisted. Lightweight Charts logical coordinates,
// TradingView logical ranges, and pixels never cross this boundary: every
// renderer resolves intent to a UTC epoch-second interval against its own
// available data before applying it.

import {
  etDateKey,
  sessionCloseEpochSeconds,
  sessionOpenEpochSeconds,
} from './marketHours';

export const CHART_TIME_WINDOW_SCHEMA_VERSION = 1;

export const WINDOW_MODE = Object.freeze({
  SESSION: 'session',
  LATEST: 'latest',
  ABSOLUTE: 'absolute',
});

export const RESOLUTION_STATUS = Object.freeze({
  EXACT: 'exact',
  CLAMPED: 'clamped',
  UNAVAILABLE: 'unavailable',
});

export const DEFAULT_BAR_INTERVAL_SECONDS = 60;
export const DEFAULT_VISIBLE_SECONDS = 3 * 60 * 60;
export const MIN_VISIBLE_BARS = 2;
export const PLOT_PAN_THRESHOLD_PX = 4;

// A drag that lands within ~1.5 bars of an edge is the user asking for that
// edge, not for an arbitrary interior point. Without this the time scale's own
// sub-pixel rounding would flip `session` into `absolute` on every repaint.
const EDGE_TOLERANCE_BARS = 1.5;
const MIN_EDGE_TOLERANCE_SECONDS = 30;

const MIN_OVERLAP_BARS = 3;
const MIN_OVERLAP_SECONDS = 60;

export function secondsFromMs(ms) {
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}

export function msFromSeconds(seconds) {
  return Number.isFinite(seconds) ? seconds * 1000 : null;
}

function finiteSeconds(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : null;
}

export function normalizeBarIntervalSeconds(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_BAR_INTERVAL_SECONDS;
}

// Sampling the tail rather than the whole series keeps this O(1) on a hot path:
// it is called on every published range change, and a session's bar spacing is
// uniform enough that the most recent bars characterise it. The median still
// absorbs gaps within the sample.
const BAR_INTERVAL_SAMPLE = 64;

export function inferBarIntervalSeconds(epochSeconds) {
  if (!Array.isArray(epochSeconds) || epochSeconds.length < 2) {
    return DEFAULT_BAR_INTERVAL_SECONDS;
  }
  const start = Math.max(1, epochSeconds.length - BAR_INTERVAL_SAMPLE);
  const deltas = [];
  for (let index = start; index < epochSeconds.length; index += 1) {
    const delta = epochSeconds[index] - epochSeconds[index - 1];
    if (Number.isFinite(delta) && delta > 0) deltas.push(delta);
  }
  if (!deltas.length) return DEFAULT_BAR_INTERVAL_SECONDS;
  deltas.sort((a, b) => a - b);
  return deltas[Math.floor(deltas.length / 2)];
}

export function edgeToleranceSeconds(barInterval) {
  return Math.max(
    MIN_EDGE_TOLERANCE_SECONDS,
    normalizeBarIntervalSeconds(barInterval) * EDGE_TOLERANCE_BARS
  );
}

export function minOverlapSeconds(barInterval) {
  return Math.max(
    MIN_OVERLAP_SECONDS,
    normalizeBarIntervalSeconds(barInterval) * MIN_OVERLAP_BARS
  );
}

export function normalizeExtent(extent) {
  const first = finiteSeconds(extent?.first);
  const last = finiteSeconds(extent?.last);
  if (first == null || last == null || last <= first) return null;
  return { first, last };
}

// Blank space reserved past the domain end so a marker centred on the final
// column renders whole instead of being half-clipped. Expressed in bar widths --
// the unit both renderers use natively -- so it scales with zoom rather than
// being pinned to minutes or pixels. Nothing is drawn here: no cells, no grid,
// no labels.
export const RIGHT_GUTTER_BARS = 10;

export function rightGutterBarsForContentSpan(contentSpan, sessionSpan) {
  if (!Number.isFinite(contentSpan) || contentSpan <= 0) return 0;
  if (!Number.isFinite(sessionSpan) || sessionSpan <= 0) return RIGHT_GUTTER_BARS;
  return contentSpan * RIGHT_GUTTER_BARS / sessionSpan;
}

export function rightGutterBarsForVisibleSpan(visibleSpan, sessionSpan) {
  if (!Number.isFinite(visibleSpan) || visibleSpan <= 0
    || !Number.isFinite(sessionSpan) || sessionSpan <= 0) {
    return RIGHT_GUTTER_BARS;
  }
  return visibleSpan * RIGHT_GUTTER_BARS / (sessionSpan + RIGHT_GUTTER_BARS);
}

/**
 * The fixed 09:30-16:00 ET regular-session domain for the day containing
 * `epochMs`.
 *
 * `session` intent resolves to this rather than to the data actually loaded, so
 * the axis reserves the whole trading day from the open and horizontal geometry
 * stays put as snapshots arrive. Making the mode definitionally clock-based is
 * also what keeps its classification tolerance meaningful: keying off the latest
 * *available* point would reclassify the same view every time new data landed.
 */
export function sessionDomainSeconds(epochMs) {
  const reference = Number.isFinite(epochMs) ? epochMs : Date.now();
  return {
    first: sessionOpenEpochSeconds(reference),
    last: sessionCloseEpochSeconds(reference),
  };
}

/**
 * Pull an extent's right edge back to the 16:00 ET close.
 *
 * Every chart anchors "latest" to this, so a late print -- index options run to
 * 16:15, futures-derived bars later still -- cannot drag the visible window past
 * the close and leave a dead sliver on the right. Data before the extent's start
 * is left alone; an extent entirely after the close is unusable.
 */
export function clampExtentToSessionClose(extent, now) {
  const normalized = normalizeExtent(extent);
  if (!normalized) return null;
  const close = sessionCloseEpochSeconds(
    now ?? normalized.last * 1000
  );
  if (normalized.last <= close) return normalized;
  if (close <= normalized.first) return null;
  return { first: normalized.first, last: close };
}

/**
 * Does an applied right edge earn the blank gutter?
 *
 * Only at the two anchors the classifier recognises: the last plotted point, and
 * the session-domain end. Inside the data there is nothing to pad, and padding
 * an interior range widens it by ten bars of real data. At either anchor the
 * space past the edge is empty, so a marker centred on the final column would be
 * clipped by the frame without it.
 *
 * Both anchors are needed, not just the last point. SPXGamma maps to the 16:00
 * cash close while index options print past it, so a `session` view ends at
 * 16:00 with a minute or two of data beyond -- enough for a bare "is anything
 * drawn past this edge" test to suppress the gutter on exactly the view that
 * most needs it.
 */
export function edgeTakesGutter(rangeTo, lastPointSeconds, barIntervalSeconds) {
  const to = finiteSeconds(rangeTo);
  if (to == null) return false;
  const tolerance = edgeToleranceSeconds(barIntervalSeconds);
  const last = finiteSeconds(lastPointSeconds);
  if (last != null && to >= last - tolerance) return true;
  return to >= sessionCloseEpochSeconds(to * 1000) - tolerance;
}

/**
 * The largest right offset, in bar widths, that still keeps the visible right
 * edge inside the session close plus the blank gutter.
 *
 * Not a constant, because `session` intent legitimately reserves the rest of the
 * trading day: mid-morning the final bar sits hours from the 16:00 edge, and a
 * flat gutter-sized ceiling would snap that reservation shut the moment it was
 * applied. The close is taken from the extent's own date, never from today --
 * before the open, at a weekend, or on hydrated prior-session bars, today's
 * close is hours or days past the final bar and the ceiling would permit exactly
 * the empty expanse it exists to prevent.
 */
export function maxRightOffsetBars(extent, barIntervalSeconds) {
  const normalized = normalizeExtent(extent);
  if (!normalized) return RIGHT_GUTTER_BARS;
  const close = sessionCloseEpochSeconds(normalized.last * 1000);
  if (!(close > normalized.last)) return RIGHT_GUTTER_BARS;
  const interval = normalizeBarIntervalSeconds(barIntervalSeconds);
  return Math.ceil((close - normalized.last) / interval) + RIGHT_GUTTER_BARS;
}

/**
 * Select the right edge for an interactive gesture.
 *
 * Session uses the session close. All other modes use the last plotted bar.
 * The proportional gutter pads the selected anchor.
 */
export function interactiveRightEdgeSeconds(
  range,
  extent,
  barIntervalSeconds,
  anchorMode = WINDOW_MODE.LATEST
) {
  const normalizedRange = normalizeRange(range);
  const normalizedExtent = normalizeExtent(extent);
  if (!normalizedRange || !normalizedExtent) return null;

  const interval = normalizeBarIntervalSeconds(barIntervalSeconds);
  const domain = sessionDomainSeconds(normalizedExtent.last * 1000);
  const sessionSpan = domain.last - domain.first;
  const candidateSpan = normalizedRange.to - normalizedRange.from;
  const gutterBars = Math.min(
    RIGHT_GUTTER_BARS,
    rightGutterBarsForVisibleSpan(candidateSpan / interval, sessionSpan / interval)
  );
  const anchor = anchorMode === WINDOW_MODE.SESSION
    ? domain.last
    : normalizedExtent.last;
  return anchor + gutterBars * interval;
}

export function extentFromEpochSeconds(epochSeconds) {
  if (!Array.isArray(epochSeconds) || !epochSeconds.length) return null;
  return normalizeExtent({
    first: epochSeconds[0],
    last: epochSeconds[epochSeconds.length - 1],
  });
}

function normalizeRange(range) {
  const from = finiteSeconds(range?.from);
  const to = finiteSeconds(range?.to);
  if (from == null || to == null || to <= from) return null;
  return { from, to };
}

/**
 * Is a visible right edge anchored to `anchor`?
 *
 * Asymmetric on purpose. Below the anchor the ordinary edge tolerance applies,
 * but above it the whole blank gutter counts as anchored: the gutter is padding
 * the renderers add programmatically and nothing is ever drawn in it, so a right
 * edge sitting inside it is the same view. Without this, every gutter is ten bar
 * widths against a tolerance of one and a half, so any republish of an applied
 * `session` or `latest` view degraded to `absolute`.
 */
function anchoredAtRightEdge(to, anchor, barIntervalSeconds) {
  const tolerance = edgeToleranceSeconds(barIntervalSeconds);
  const gutter = RIGHT_GUTTER_BARS * normalizeBarIntervalSeconds(barIntervalSeconds);
  return to >= anchor - tolerance && to <= anchor + gutter + tolerance;
}

/**
 * Classify a user-driven visible range against the source chart's available
 * data. Returns bare intent; callers add persistence metadata.
 */
export function classifyVisibleRange(
  range,
  extent,
  { barIntervalSeconds, isDefaultSpan, positionIsIntent } = {}
) {
  const normalizedRange = normalizeRange(range);
  const normalizedExtent = normalizeExtent(extent);
  if (!normalizedRange || !normalizedExtent) return null;

  const tolerance = edgeToleranceSeconds(barIntervalSeconds);
  const atLatest = anchoredAtRightEdge(
    normalizedRange.to,
    normalizedExtent.last,
    barIntervalSeconds
  );
  // Session is the fixed clock domain, not the loaded data. Checked first so a
  // full-day view at the close, where the domain end and the latest point
  // coincide, reads as `session` rather than `latest`.
  const domain = sessionDomainSeconds(normalizedExtent.last * 1000);
  const spansDomain = Math.abs(normalizedRange.from - domain.first) <= tolerance
    && anchoredAtRightEdge(normalizedRange.to, domain.last, barIntervalSeconds);
  if (spansDomain) return { mode: WINDOW_MODE.SESSION };

  // A Session view reserves the future part of the trading day. Preserve a
  // zoom wholly inside that region because it has no data span to re-anchor.
  if (normalizedRange.from >= normalizedExtent.last) {
    return {
      mode: WINDOW_MODE.ABSOLUTE,
      from: normalizedRange.from,
      to: normalizedRange.to,
      ...(positionIsIntent ? { positionPinned: true } : {}),
    };
  }

  // A pan moves the window without changing its span, so where it landed is
  // the whole intent. Re-anchoring it to the data edge -- correct for a zoom,
  // which only expresses a duration -- would leave every other chart showing a
  // window the user never dragged to. A pan that ends at the data edge is
  // excluded: `latest` keeps it following new bars, which is what the gesture
  // asked for.
  if (positionIsIntent && !atLatest) {
    return {
      mode: WINDOW_MODE.ABSOLUTE,
      from: normalizedRange.from,
      to: normalizedRange.to,
      // `absolute` otherwise carries two different intents: a seek that landed
      // on a Custom window, which a later scrub is right to reset to Latest,
      // and a position someone panned to, which it is not. Recording which one
      // this is keeps replay from resetting the second.
      positionPinned: true,
    };
  }

  // Otherwise a gesture changes only the span: the renderer re-anchors the
  // result to the data edge. ABSOLUTE also covers legacy restore and replay.
  const measuredSpan = Math.min(normalizedRange.to, normalizedExtent.last)
    - normalizedRange.from;
  if (!(measuredSpan > 0)) return null;
  const minimumSpan = normalizeBarIntervalSeconds(barIntervalSeconds) * MIN_VISIBLE_BARS;
  const reachesSessionOpen = atLatest
    && normalizedRange.from <= domain.first + tolerance;
  const durationSeconds = Math.max(
    measuredSpan,
    minimumSpan,
    reachesSessionOpen ? DEFAULT_VISIBLE_SECONDS : 0
  );
  return {
    mode: WINDOW_MODE.LATEST,
    durationSeconds,
    isDefaultSpan: typeof isDefaultSpan === 'boolean'
      ? isDefaultSpan
      : Math.abs(durationSeconds - DEFAULT_VISIBLE_SECONDS) <= tolerance,
  };
}

export function isCustomWindowPreference(preference) {
  if (preference?.mode === WINDOW_MODE.ABSOLUTE) return true;
  if (preference?.mode !== WINDOW_MODE.LATEST) return false;
  if (typeof preference.isDefaultSpan === 'boolean') return !preference.isDefaultSpan;
  return Number(preference.durationSeconds) !== DEFAULT_VISIBLE_SECONDS;
}

/**
 * Attach persistence metadata to bare intent. `tradingDate` records the ET
 * session the intent was expressed in so a stale absolute interval can be
 * recognized later.
 */
export function createWindowPreference(intent, { now = Date.now(), tradingDate } = {}) {
  if (!intent?.mode) return null;
  const base = {
    version: CHART_TIME_WINDOW_SCHEMA_VERSION,
    mode: intent.mode,
    tradingDate: tradingDate ?? etDateKey(now),
    updatedAt: now,
  };

  if (intent.mode === WINDOW_MODE.SESSION) return normalizeWindowPreference(base);
  if (intent.mode === WINDOW_MODE.LATEST) {
    return normalizeWindowPreference({
      ...base,
      durationSeconds: intent.durationSeconds,
      isDefaultSpan: intent.isDefaultSpan,
    });
  }
  if (intent.mode === WINDOW_MODE.ABSOLUTE) {
    return normalizeWindowPreference({
      ...base,
      from: intent.from,
      to: intent.to,
      positionPinned: intent.positionPinned,
    });
  }
  return null;
}

export function normalizeWindowPreference(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.version !== CHART_TIME_WINDOW_SCHEMA_VERSION) return null;

  const tradingDate = typeof raw.tradingDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw.tradingDate)
    ? raw.tradingDate
    : null;
  const updatedAt = Number.isFinite(Number(raw.updatedAt)) ? Number(raw.updatedAt) : null;
  if (!tradingDate || updatedAt == null) return null;

  const base = {
    version: CHART_TIME_WINDOW_SCHEMA_VERSION,
    mode: raw.mode,
    tradingDate,
    updatedAt,
  };

  if (raw.mode === WINDOW_MODE.SESSION) return base;

  if (raw.mode === WINDOW_MODE.LATEST) {
    const durationSeconds = finiteSeconds(raw.durationSeconds);
    if (durationSeconds == null || durationSeconds <= 0) return null;
    const isDefaultSpan = typeof raw.isDefaultSpan === 'boolean'
      ? raw.isDefaultSpan
      : durationSeconds === DEFAULT_VISIBLE_SECONDS;
    return { ...base, durationSeconds, isDefaultSpan };
  }

  if (raw.mode === WINDOW_MODE.ABSOLUTE) {
    const range = normalizeRange(raw);
    if (!range) return null;
    return {
      ...base,
      from: range.from,
      to: range.to,
      positionPinned: raw.positionPinned === true,
    };
  }

  return null;
}

export function sameWindowPreference(a, b) {
  if (!a || !b) return a === b;
  if (a.mode !== b.mode) return false;
  if (a.mode === WINDOW_MODE.SESSION) return true;
  if (a.mode === WINDOW_MODE.LATEST) {
    return a.durationSeconds === b.durationSeconds
      && a.isDefaultSpan === b.isDefaultSpan;
  }
  return a.from === b.from
    && a.to === b.to
    && Boolean(a.positionPinned) === Boolean(b.positionPinned);
}

function unavailable() {
  return { status: RESOLUTION_STATUS.UNAVAILABLE, range: null };
}

/**
 * Resolve stored intent against a target chart's available extent.
 *
 * - `exact`: the target fully covers the requested interval.
 * - `clamped`: only partial overlap; render the overlap but keep the stored
 *   preference untouched so the originating chart still honours it.
 * - `unavailable`: no meaningful overlap or invalid input; the caller keeps its
 *   own default and must not mutate the store.
 */
export function resolveWindowPreference(preference, extent, { barIntervalSeconds } = {}) {
  const pref = normalizeWindowPreference(preference);
  const normalizedExtent = normalizeExtent(extent);
  if (!pref || !normalizedExtent) return unavailable();

  if (pref.mode === WINDOW_MODE.SESSION) {
    // The whole trading day, reserved from the open, regardless of how much of
    // it has arrived. Anchored to the target's own data date so the intent
    // carries across sessions.
    const domain = sessionDomainSeconds(normalizedExtent.last * 1000);
    return {
      status: RESOLUTION_STATUS.EXACT,
      range: { from: domain.first, to: domain.last },
    };
  }

  if (pref.mode === WINDOW_MODE.LATEST) {
    // Always < last: durationSeconds is positive and the extent is non-empty.
    const from = Math.max(normalizedExtent.first, normalizedExtent.last - pref.durationSeconds);
    // A target holding less history than the requested duration still honours
    // the intent -- rolling-latest means "ending at latest", not a fixed span.
    const status = normalizedExtent.last - pref.durationSeconds < normalizedExtent.first
      ? RESOLUTION_STATUS.CLAMPED
      : RESOLUTION_STATUS.EXACT;
    return { status, range: { from, to: normalizedExtent.last } };
  }

  const tolerance = edgeToleranceSeconds(barIntervalSeconds);
  const domain = sessionDomainSeconds(normalizedExtent.last * 1000);
  const sameSession = pref.tradingDate === etDateKey(normalizedExtent.last * 1000);
  const withinSessionDomain = pref.from >= domain.first - tolerance
    && anchoredAtRightEdge(pref.to, domain.last, barIntervalSeconds);
  if (sameSession && withinSessionDomain) {
    return {
      status: RESOLUTION_STATUS.EXACT,
      range: { from: pref.from, to: pref.to },
    };
  }

  // An interval whose right edge runs past the loaded data is a window the
  // session is growing into, not a historical interval this target only partly
  // holds. `clamped` is for the latter: applied here it would report the window
  // unavailable while it sat wholly in the reserved future region, then, as
  // live bars crossed its left edge, collapse it to the handful of minutes that
  // had arrived -- a two-hour window down to three (the `minOverlapSeconds`
  // floor) on one bar -- and widen it back only over the hours it takes the
  // session to fill. The preference is shared and persisted, so every chart
  // would follow. Honour it as expressed while it stays inside the same
  // session's clock domain; once the data covers it, `covered` below returns
  // the same interval and this branch stops firing.
  //
  // Keyed on the right edge, not the left: the left edge is what the data
  // crosses first, so testing it is what made the window collapse.
  const growsIntoSession = pref.to > normalizedExtent.last
    && pref.from >= domain.first - tolerance
    && pref.to <= domain.last
      + RIGHT_GUTTER_BARS * normalizeBarIntervalSeconds(barIntervalSeconds)
      + tolerance;
  if (sameSession && growsIntoSession) {
    return {
      status: RESOLUTION_STATUS.EXACT,
      range: { from: pref.from, to: pref.to },
    };
  }

  const overlapFrom = Math.max(pref.from, normalizedExtent.first);
  const overlapTo = Math.min(pref.to, normalizedExtent.last);
  if (overlapTo - overlapFrom < minOverlapSeconds(barIntervalSeconds)) return unavailable();

  const covered = pref.from >= normalizedExtent.first - tolerance
    && pref.to <= normalizedExtent.last + tolerance;
  if (covered) {
    return { status: RESOLUTION_STATUS.EXACT, range: { from: pref.from, to: pref.to } };
  }
  return {
    status: RESOLUTION_STATUS.CLAMPED,
    range: { from: overlapFrom, to: overlapTo },
  };
}
